// POPIA constants — single source of truth for the Responsible Party
// identity, the named Information Officer, the SAR contact email, and
// the current privacy-policy version string. Update these here and the
// /privacy page + Login + AcceptInvite tick-boxes + SAR mailto links
// stay in sync.
//
// When the policy text materially changes, bump POLICY_VERSION (e.g.
// 'privacy_policy_v3'). Existing users see ConsentGate once on their
// next sign-in, because it looks for the NEW key in users.consents.
// Earlier acceptances stay on record under their own keys: the
// record_user_consent RPC adds the new key and never touches the old
// ones. ('<version>__history' only collects a re-acceptance of the SAME
// version.) Procedure: docs/runbooks/POPIA-OPERATIONS.md §3.

export const RESPONSIBLE_PARTY = {
  // Platform-owning legal entity. Per POPIA, the Responsible Party is
  // the entity that determines the purpose + means of processing — i.e.
  // who decides what the platform stores + why. For platform-level
  // processing this is Big Berries. For each external tenant, Big
  // Berries is an Operator (processor) acting on the tenant's
  // instructions; see the Operator Agreement template referenced in
  // docs/runbooks/POPIA-OPERATIONS.md.
  legalName:      'Big Berries (Pty) Ltd',
  // The natural-person Information Officer required by POPIA s56. The
  // head of the responsible party is the IO by default — Jason here.
  // Add a deputy when the team grows beyond a handful of admins.
  informationOfficer: {
    name:   'Jason Webber',
    title:  'Information Officer',
  },
  // SAR + privacy contact. Used in the privacy policy + the mailto
  // links + the placeholder in the Information Regulator registration
  // form. Set up the alias on the platform-entity domain so it routes
  // to a tracked inbox.
  contactEmail:   'privacy@bigberries.co.za',
  // Country of registration. Affects which cross-border-transfer
  // disclosures apply.
  jurisdiction:   'South Africa',
} as const

// Current privacy-policy version slug. Stored as the top-level key in
// users.consents when a user accepts. Bumping this string forces every
// active user to re-consent on next login.
//
// v1.1 (2026-06-10) added the anonymous-usage-telemetry disclosure (mig
// 805): processed under legitimate interest (s11(1)(f)), anonymous by
// design — a transparency addition, not a new consent basis — so the
// slug stays at v1 and no re-consent interrupt is forced.
//
// v2 (2026-10-03) — Jason's ruling, 3 Oct: one accurate policy. Names
// every AI feature that sends data to a model vendor (adds the two
// Compliance Pro features; Ask's unredacted attendance data; the OpenAI
// voice is the default), adds the two sub-processors the policy never
// named — Resend (all email) and TextMeBot (all WhatsApp; it replaces a
// "Twilio / Meta" line no code uses) — and drops an unverified Vercel DPA
// claim. New sub-processors, so every user re-accepts once.
//
// v2.1 (2026-10-04) — Jason's ruling, 4 Oct: section 2 names optional Sign
// in with Google / Microsoft (PR #2997). Those companies act for the user,
// not for us — no new sub-processor, no new consent basis — so, like v1.1,
// a transparency addition: the slug stays at v2 and nobody is interrupted.
//
// v3 (2026-10-12) — Jason's ruling, 12 Oct: Ask BigAgri's read-aloud voice
// moves to Eleven Labs Inc. (OpenAI stays as the backup voice). ElevenLabs is a
// new sub-processor, so every user re-accepts once. The ElevenLabs PHONE agent
// is not named: it is sandbox-only and not live for any business.
export const POLICY_VERSION = 'privacy_policy_v3' as const

// Human-readable label for the policy version. Shown in the policy header,
// on ConsentGate, on AcceptInvite and in the SAR export.
export const POLICY_VERSION_LABEL = 'v3, 12 October 2026' as const

// Effective date — first day this version of the policy is in force.
// Used in the policy page header. Update when POLICY_VERSION is bumped.
export const POLICY_EFFECTIVE_DATE = '2026-10-12' as const

// ---------------------------------------------------------------------------
// Data-sharing (cross-tenant benchmark) consent — Phase 0D. A SEPARATE version
// track from the privacy policy; bump independently when these terms change.
// The exact text below is what the tenant consents to — the UI hashes it
// (SHA-256) into consent_grants.consent_text_hash as proof of WHAT was agreed,
// so the wording is load-bearing: changing it without bumping the version would
// break the binding. Keep the version + text in lock-step.
// ---------------------------------------------------------------------------
export const DATA_SHARING_VERSION = 'data_sharing_v1' as const
export const DATA_SHARING_VERSION_LABEL = 'v1, 6 June 2026' as const
export const DATA_SHARING_TERMS =
  'By enabling this, you agree that the platform may include your operation’s ' +
  'production data (volumes, throughput, quality and timing) in anonymised, ' +
  'aggregated industry benchmarks shared with other participants and partners. ' +
  'Your data is only ever shown combined with other operations — never identified ' +
  'individually — and any aggregate covering fewer than three operations is suppressed. ' +
  'You can withdraw at any time; withdrawal stops all future use immediately, though ' +
  'benchmarks already published cannot be recalled.'

// ---------------------------------------------------------------------------
// Earned-Wage Access consent — Phase 0D / EWA. Separate per-feature version
// track (consent_required_version('EWA_ADVANCE') must equal EWA_VERSION). The
// terms are hashed into consent_grants.consent_text_hash as proof; bump the
// version in lock-step with any wording change.
// ---------------------------------------------------------------------------
export const EWA_VERSION = 'ewa_advance_v1' as const
export const EWA_VERSION_LABEL = 'v1, 7 June 2026' as const
export const EWA_TERMS =
  'I agree that my employer may advance me a portion of the wages I have already ' +
  'earned in the current pay period, and recover that amount from my next pay. ' +
  'The advance is interest-free and fee-free — I repay only what I drew. I ' +
  'understand I can stop using Earned-Wage Access at any time, and that drawing ' +
  'an advance does not change my total pay, only when part of it reaches me.'
